import { useMemo } from 'react';

import { useI18n } from '~/hooks/use-i18n';

import { RAW_DURATION_LABELS } from '../constants';

import { useCanAccessLearningPlans } from './use-can-access-learning-plans';

export function useCatalogFilterOptions({ isMastersFilterEnabled = false }: { isMastersFilterEnabled?: boolean } = {}) {
  const { t } = useI18n();
  const canAccessLearningPlans = useCanAccessLearningPlans();

  return useMemo(
    () => ({
      price: [
        { label: t('catalog.filters.option.free'), value: 'free' },
        { label: t('catalog.filters.option.paid'), value: 'paid' },
      ],
      difficulty: [
        { label: t('catalog.filters.option.beginner'), value: 'beginner' },
        { label: t('catalog.filters.option.intermediate'), value: 'intermediate' },
        { label: t('catalog.filters.option.advanced'), value: 'advanced' },
      ],
      school: [
        { label: t('common.schoolNames.dataScience'), value: 'data science' },
        { label: t('common.schoolNames.autonomousSystems'), value: 'autonomous systems' },
        { label: t('common.schoolNames.artificialIntelligence'), value: 'artificial intelligence' },
        { label: t('common.schoolNames.business'), value: 'business' },
        { label: t('common.schoolNames.programming'), value: 'programming and development' },
        { label: t('common.schoolNames.executiveLeadership'), value: 'executive leadership' },
        { label: t('common.schoolNames.productManagement'), value: 'product management' },
        { label: t('common.schoolNames.cybersecurity'), value: 'cybersecurity' },
        { label: t('common.schoolNames.cloudComputing'), value: 'cloud computing' },
        { label: t('common.schoolNames.careerResources'), value: 'Career resources' }, // "Career resources" is defined in uppercase in the taxonomy.
      ],
      scope: [
        { label: t('catalog.filters.option.all'), value: 'all' },
        { label: t('catalog.filters.option.favorite'), value: 'favorite' },
        { label: t('catalog.filters.option.enrolled'), value: 'enrolled' },
        { label: t('catalog.filters.option.completed'), value: 'completed' },
      ],
      type: [
        { label: t('program.semanticType.course'), value: 'course' },
        { label: t('program.semanticType.degree'), value: 'nanodegree program' },
        ...(isMastersFilterEnabled ? [{ label: t('program.mastersDegree'), value: 'masters' }] : []),
        ...(canAccessLearningPlans ? [{ label: t('learningPlan.learningPlan'), value: 'Learning_Plan' }] : []),
      ],
      availability: [
        { label: t('catalog.filters.option.enrollNow'), value: 'Enroll Now' },
        { label: t('catalog.filters.option.requestAccess'), value: 'Request Access' },
      ],
      rawDuration: [
        { label: t('catalog.filters.option.raw.lessThan20'), value: RAW_DURATION_LABELS.LT20 },
        { label: t('catalog.filters.option.raw.between20And60'), value: RAW_DURATION_LABELS.MID },
        { label: t('catalog.filters.option.raw.greaterThan60'), value: RAW_DURATION_LABELS.GT60 },
      ],
      language: [
        // only used when user's language is Japanese
        { label: 'すべての言語', value: 'all' },
        { label: '日本語', value: 'ja' },
      ],
    }),
    [t, canAccessLearningPlans, isMastersFilterEnabled]
  );
}
